import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MeasuringStrategy,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  defaultAnimateLayoutChanges,
  rectSortingStrategy,
  SortableContext,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getMediaObjects } from "senf-shared";
import type { Project } from "senf-shared/types";
import { tv } from "tailwind-variants";
import Button from "../../../core/button/Button";
import Icon from "../../../core/icon/Icon";
import type { Question } from "../../../special/survey/legacy/survey.types";
import type { ImageInput } from "../../imageModifier/ImagesUploadModal";
import ModifyImageButtons from "../../imageModifier/ModifyImageButtons";
import { useOnDragEnd } from "../../pageBuilder/SortableSections";
import { useVerifyFiles } from "../legacy/hooks";
import type { useEditQuestion } from "../legacy/utils";
import type { Page, SurveyLogo } from "../survey.config";

const SortableLogoStyles = tv({
  slots: {
    Outer: "flex w-full flex-col",
    Wrapper: "w-full rounded-xl",
    Grid: "flex flex-wrap gap-x-10 gap-y-12 relative",
    Option: "relative group/option flex items-center justify-center min-h-10",
    ImageWrapper: "",
    RemoveItemButton:
      "absolute -right-3 -top-3 z-10 bg-white opacity-0 shadow-md group-hover/option:opacity-100",
  },
  variants: {
    alignment: {
      center: {
        Grid: "justify-center",
      },
    },
    disabled: {
      false: {
        Wrapper: "group/wrapper",
        ImageWrapper:
          "cursor-move group-hover/wrapper:bg-surface-default group-hover/wrapper:ring-8 rounded ring-surface-default",
      },
      true: {
        Option: "pointer-events-auto",
      },
    },
  },
});

interface LogoProps {
  logos: SurveyLogo[];
  mode: "edit" | "display";
  project?: Project;
  filePath?: string;
  setLogos?: React.Dispatch<React.SetStateAction<SurveyLogo[]>>;
  saveToPath?: string;
  alignment?: "center";
  size?: number;
}

interface LogoItemProps {
  project?: Project;
  id: string;
  dimensions: Record<
    string,
    {
      width: number;
      height: number;
    }
  >;
  logo: SurveyLogo;
  mode: "edit" | "display";
  index?: number;
  removeItem?: (uid: string) => Promise<void>;
  handleFileChange?: (files: ImageInput[], uid: string) => Promise<void>;
  removable?: boolean;
  loading?: boolean;
  saveToPath?: string;
}

type Dimensions = { width: number; height: number };

function balanceLogo(logo: Dimensions, targetArea = 5000) {
  // Compute original area and scale factor to normalize area to targetArea
  const origArea = logo.width * logo.height;
  const scaleFactor = Math.sqrt(targetArea / origArea);

  // Normalize dimensions so that each logo has an area equal to targetArea
  const finalWidth = logo.width * scaleFactor;
  const finalHeight = logo.height * scaleFactor;

  return {
    finalWidth,
    finalHeight,
  };
}

const LogoItem = ({
  project,
  id,
  logo,
  dimensions,
  mode,
  index,
  removeItem,
  handleFileChange,
  removable,
  loading,
  saveToPath,
}: LogoItemProps) => {
  const { t } = useTranslation();

  const {
    attributes,
    listeners,
    isDragging,
    setNodeRef,
    transform,
    transition,
    rect,
  } = useSortable({
    id,
    disabled: mode !== "edit" || !logo?.uid,
    animateLayoutChanges: (args) => {
      return defaultAnimateLayoutChanges({ ...args, wasDragging: true });
    },
  });
  const inputRef = useRef<HTMLInputElement>(null);

  // if (transform?.x) transform.x = transform.x - (rect.current?.width || 0) / 2;
  // (dimensions?.[logo?.uid]?.width || 0) / 2;
  const style = {
    opacity: isDragging && (index || Number.POSITIVE_INFINITY) > -1 ? 0 : 1,
    transform: CSS.Translate.toString(transform),
    transition,
  };

  const { Option, ImageWrapper, RemoveItemButton } = SortableLogoStyles({
    disabled: mode !== "edit" || !logo?.uid,
  });

  return (
    <div
      className={Option()}
      ref={setNodeRef}
      style={{
        ...style,
        width: dimensions?.[logo?.uid || ""]?.width || 0,
      }}
      {...attributes}
      {...listeners}
      tabIndex={mode === "edit" ? 0 : undefined}
      role={mode === "edit" ? "button" : undefined}
    >
      {mode === "edit" && (
        <div className="absolute shrink-0 self-center z-10 -bottom-2 pt-2 group-hover/option:opacity-100 opacity-0 cursor-move">
          <Icon
            name="handle-horizontal"
            className="h-5 w-7 text-icon-subdued bg-surface-default rounded-full"
          />
        </div>
      )}
      {logo && removeItem && removable && !logo.url && mode === "edit" && (
        <Button
          leadingIcon="x-md"
          size="sm"
          variant="outline"
          onClick={(e) => {
            e.stopPropagation();
            removeItem(logo.uid || "");
          }}
          className={RemoveItemButton()}
        />
      )}
      {logo ? (
        <div className="relative flex flex-col gap-2">
          {
            <div className="relative">
              {logo.url && mode === "edit" ? (
                <ModifyImageButtons
                  project={project}
                  className="opacity-0 group-hover/option:opacity-100 -right-5 -top-5 z-20"
                  inputRef={inputRef}
                  imageUrl={logo.url}
                  fileTypes={[
                    "image/png",
                    "image/jpeg",
                    "image/jpg",
                    "image/gif",
                    "image/webp",
                  ]}
                  fileTypeLabel={{
                    singular: "global:logo",
                    plural: "global:logos",
                  }}
                  altText={logo.altText}
                  onChangeImage={(files) =>
                    handleFileChange?.(files, logo.uid || "")
                  }
                  onRemoveImage={() => removeItem?.(logo.uid || "")}
                  maxItems={1}
                  saveToPath={saveToPath}
                />
              ) : null}

              {logo.url ? (
                <img
                  src={logo.url}
                  alt={logo.altText || `${t("global:logo")}`}
                  className={ImageWrapper()}
                />
              ) : mode !== "edit" ? (
                <div className="text-action-secondary-pressed ring-action-secondary-pressed bg-surface-highlight-subdued flex aspect-square w-full items-center justify-center rounded-lg ring-1">
                  <Icon name="image" className="h-8 w-8" />
                </div>
              ) : null}
            </div>
          }
        </div>
      ) : (
        <div className="text-action-primary-pressed flex h-full w-full items-center justify-center">
          <Icon name={"plus-md"} className="h-8 w-8" />
        </div>
      )}
    </div>
  );
};

const loadedImage = (url: string) =>
  new Promise<HTMLImageElement>((resolve) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.src = url;
  });

const SortableLogos = ({
  logos,
  setLogos,
  alignment,
  project,
  filePath,
  mode,
  saveToPath,
  size = 5000,
}: LogoProps) => {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const { Outer, Wrapper, Grid } = SortableLogoStyles({
    alignment,
    disabled: mode !== "edit",
  });

  const [loading, setLoading] = useState<Question["uid"][]>([]);
  const { invalidType, invalidSize, checkFileUpload } = useVerifyFiles();
  const removable = useMemo(() => {
    return !!logos?.length;
  }, [logos]);

  const handleFileChange = async (files: ImageInput, uid: string) => {
    if (!setLogos) return;
    setLoading((l) => [...new Set([...l, uid])]);

    // Handle new format (array of {image, alt} objects)
    if (Array.isArray(files) && files[0] && "url" in files[0]) {
      const { url } = files[0] as { url: string; alt: string };
      setLogos(
        logos?.map((logo) => (logo.uid === uid ? { ...logo, url } : logo)) ||
          [],
      );
      setLoading((l) => l.filter((e) => e !== uid));
      return;
    }
    const fileList = checkFileUpload({ files });
    const [object] = await getMediaObjects({
      fileList,
      setPath: () => filePath || "",
    });

    setLogos(
      logos?.map((logo) =>
        logo.uid === uid ? { ...logo, url: object.url } : logo,
      ) || [],
    );
    setLoading((l) => l.filter((e) => e !== uid));
  };

  const removeItem = (uid: string) => {
    setLogos?.(logos?.filter((logo) => logo.uid !== uid) || []);
    return Promise.resolve();
  };

  const [dimensions, setDimensions] = useState(
    logos?.reduce(
      (acc, e) => {
        acc[e.uid] = {
          width: 0,
          height: 0,
        };
        return acc;
      },
      {} as Record<string, Dimensions>,
    ) || {},
  );
  useEffect(() => {
    for (const e of logos || []) {
      loadedImage(e.url).then((image) => {
        const balanced = balanceLogo(image, size);
        setDimensions((prev) => ({
          ...prev,
          [e.uid]: {
            width: balanced.finalWidth,
            height: balanced.finalHeight,
          },
        }));
      });
    }
  }, [logos]);

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: () => {
        return {
          x: 0,
          y: 0,
        };
      },
    }),
  );

  const [activeId, setActiveId] = useState<string | null>(null);
  const handleDragStart = ({ active }) => setActiveId(active.id as string);
  const handleDragCancel = () => setActiveId(null);
  const handleDragEnd = useOnDragEnd(logos || [], (sorted) => {
    setActiveId(null);
    setLogos?.(sorted);
  });

  if (logos?.length === 0) {
    return null;
  }

  return (
    <div className={Outer()}>
      <div className={Wrapper()} ref={ref}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
          measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
        >
          <SortableContext
            items={logos?.map((item) => item.uid || "") || []}
            strategy={rectSortingStrategy}
          >
            <div className={Grid()}>
              {logos?.map((item, index) => (
                <LogoItem
                  project={project}
                  key={item.uid}
                  id={item.uid || ""}
                  logo={item}
                  dimensions={dimensions}
                  index={index}
                  handleFileChange={handleFileChange}
                  loading={loading.includes(item.uid)}
                  mode={mode}
                  removable={removable}
                  removeItem={removeItem}
                  saveToPath={saveToPath}
                />
              ))}
            </div>
          </SortableContext>

          <DragOverlay>
            <LogoItem
              project={project}
              logo={
                logos?.find((option) => option.uid === activeId) || logos?.[0]
              }
              id={activeId || ""}
              dimensions={dimensions}
              index={logos?.findIndex((option) => option.uid === activeId)}
              handleFileChange={handleFileChange}
              mode={mode}
              removable={removable}
              removeItem={removeItem}
              saveToPath={saveToPath}
            />
          </DragOverlay>
        </DndContext>
      </div>
    </div>
  );
};

export default SortableLogos;

interface SurveyLogosProps {
  page: Page<"intro" | "outro">;
  loadingImage: boolean;
  mode: "edit" | "display";
  setPages: React.Dispatch<React.SetStateAction<Page<"intro" | "outro">[]>>;
  pageIndex: number;
  imageFocus: boolean;
  project: Project;
  surveyId: string;
  updateQuestion: ReturnType<typeof useEditQuestion>["updateQuestion"];
  pageAlignment: "left" | "center" | "right";
}
export const SurveyLogos = ({
  page,
  loadingImage,
  mode,
  setPages,
  pageIndex,
  imageFocus,
  project,
  surveyId,
  ...props
}: SurveyLogosProps) => {
  const filePath = `organizationsData/${project.organizationId}/projects/${project.projectId}/surveys/${surveyId}/surveyLogosUpload/`;

  const [logos, setLogosState] = useState<SurveyLogo[]>(
    page.logos?.items || [],
  );
  const setLogos = useCallback<
    React.Dispatch<React.SetStateAction<SurveyLogo[]>>
  >(
    (logos) => {
      setLogosState((prev) => {
        const newLogos = typeof logos === "function" ? logos(prev) : logos;
        props.updateQuestion(
          {
            ...page.logos,
            items: newLogos,
          },
          "logos",
        );
        return newLogos;
      });
    },
    [props.updateQuestion, page.logos],
  );
  useEffect(() => {
    setLogosState(page.logos?.items || []);
  }, [page.logos?.items]);
  return (
    <div key={page.uid} className="py-10">
      <SortableLogos
        logos={logos}
        mode={mode}
        setLogos={setLogos}
        filePath={filePath}
        alignment={props.pageAlignment}
        project={project}
        saveToPath={`surveys/${surveyId}`}
        size={2000}
      />
    </div>
  );
};
